/**
 * The aircraft's aerodynamic panels, evaluated as a set.
 *
 * The panels are not independent: the tailplane flies in the wing's downwash, and the strength
 * of that downwash depends on how hard the wing is working *right now*. So the set is
 * evaluated in two passes:
 *
 *   1. **Upstream** — the wing (and the fuselage), in clean air. Their lift is summed.
 *   2. **Downstream** — the tail surfaces, in air deflected downward by
 *      `ε = 2·CL_wing / (π·AR)`, the classical far-field downwash behind a finite wing.
 *
 * Two passes rather than one lagged tick because the trim solver needs a converged answer, and
 * because a lag in the wing–tail coupling shows up as a small phantom pitch oscillation.
 *
 * This coupling is not a detail. Without it the tailplane sees the full free-stream angle of
 * attack, so a nose-heavy aeroplane runs out of up-elevator well before the wing reaches
 * maximum lift, and the modelled stall speed comes out ~30 % high. It is also the mechanism
 * behind the nose-down pitch when flaps are lowered: more lift, more downwash, less tail
 * angle of attack, less download.
 *
 * `updateFixed(dt)`: called from the FDM's tick. Allocation-free.
 */

import type { Vector3 } from 'three';
import type { AeroPanel, FlowContext } from './panel.js';

export interface AeroTotals {
  /** Sum of every panel's lift magnitude [N] — used for the whole-aircraft CL. */
  totalLiftN: number;
  /** Lift from the downwash-generating (wing) panels only [N]. */
  wingLiftN: number;
  /** Wing lift coefficient this tick. */
  wingLiftCoefficient: number;
  /** Downwash angle at the tail [rad]. */
  downwashRad: number;
  /** Is any downwash-generating panel past its stall angle? */
  stallWarning: boolean;
}

export function makeAeroTotals(): AeroTotals {
  return {
    totalLiftN: 0,
    wingLiftN: 0,
    wingLiftCoefficient: 0,
    downwashRad: 0,
    stallWarning: false,
  };
}

export class AeroAssembly {
  /** Panels evaluated in clean air, whose lift drives the downwash. */
  private readonly upstream: AeroPanel[] = [];
  /** Panels evaluated in the wing's downwash. */
  private readonly downstream: AeroPanel[] = [];

  private readonly wingAreaM2: number;
  private readonly aspectRatio: number;

  readonly totals = makeAeroTotals();

  constructor(panels: readonly AeroPanel[], wingAreaM2: number, wingSpanM: number) {
    for (const panel of panels) {
      // Anything that flies in the downwash must be evaluated after whatever creates it.
      if (panel.receivesDownwash) this.downstream.push(panel);
      else this.upstream.push(panel);
    }
    this.wingAreaM2 = wingAreaM2;
    this.aspectRatio = (wingSpanM * wingSpanM) / wingAreaM2;
  }

  /** Every panel, in evaluation order. */
  get panels(): readonly AeroPanel[] {
    return [...this.upstream, ...this.downstream];
  }

  /**
   * Accumulate the whole aircraft's aerodynamic force and moment about the centre of gravity.
   * `context.downwashMps` is computed here and overwritten; callers need not set it.
   * Allocation-free.
   */
  accumulate(context: FlowContext, forceAccumulator: Vector3, momentAccumulator: Vector3): AeroTotals {
    const totals = this.totals;
    totals.totalLiftN = 0;
    totals.wingLiftN = 0;
    totals.stallWarning = false;

    // --- pass 1: clean air --------------------------------------------------------------
    context.downwashMps = 0;
    for (let i = 0; i < this.upstream.length; i++) {
      const panel = this.upstream[i];
      panel.accumulate(context, forceAccumulator, momentAccumulator);
      const diagnostics = panel.diagnostics;
      totals.totalLiftN += diagnostics.liftN;
      if (panel.contributesDownwash) {
        totals.wingLiftN += diagnostics.liftN;
        if (diagnostics.stalled) totals.stallWarning = true;
      }
    }

    // --- downwash from the wing's own lift -------------------------------------------------
    const speedSquared = context.velocityBody.lengthSq();
    const dynamicPressure = 0.5 * context.densityKgPerM3 * speedSquared;
    const wingCl = dynamicPressure > 1 ? totals.wingLiftN / (dynamicPressure * this.wingAreaM2) : 0;
    totals.wingLiftCoefficient = wingCl;
    const downwashRad = (2 * wingCl) / (Math.PI * this.aspectRatio);
    totals.downwashRad = downwashRad;
    context.downwashMps = downwashRad * Math.sqrt(speedSquared);

    // --- pass 2: in the downwash ------------------------------------------------------------
    for (let i = 0; i < this.downstream.length; i++) {
      const panel = this.downstream[i];
      panel.accumulate(context, forceAccumulator, momentAccumulator);
      totals.totalLiftN += panel.diagnostics.liftN;
    }

    return totals;
  }
}
